// Geo targeting helpers. Mirrors src/utils/geo.ts on the server. The API
// returns geo as a list (["AU","GB"], ["ANY"], or [] when unset) and accepts
// a list on write; it's stored as "AU,GB" / "ANY".

export const ANY_GEO = 'ANY';

export const ISO_GEO_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
  'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP ' +
  'KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT ' +
  'MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW ' +
  'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG ' +
  'UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW EU'
).split(' ');

const ISO_SET = new Set(ISO_GEO_CODES);

// SVG flags from flag-icons, emitted as separate assets so the browser only
// fetches the ones on screen. (Flag emoji don't render on Windows.)
const FLAG_URLS: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob('../../node_modules/flag-icons/flags/4x3/*.svg', { eager: true, query: '?url', import: 'default' }) as Record<string, string>,
  ).map(([path, url]) => [path.slice(path.lastIndexOf('/') + 1, -4).toUpperCase(), url]),
);

export function flagUrl(code: string): string | null {
  return FLAG_URLS[code.toUpperCase()] ?? null;
}

let regionNames: Intl.DisplayNames | null = null;
try { regionNames = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { /* old browser */ }

/** Human name for a code: "GB" → "United Kingdom", "ANY" → "Any geo". */
export function geoName(code: string): string {
  if (code === ANY_GEO) return 'Any geo';
  if (code === 'EU') return 'European Union';
  if (code === 'XK') return 'Kosovo';
  if (!ISO_SET.has(code)) return code;
  try { return regionNames?.of(code) ?? code; } catch { return code; }
}

export function isIsoGeo(code: string): boolean {
  return ISO_SET.has(code);
}

/** Codes from an API value (list) or a comma-separated string (filter options, legacy). */
export function parseStoredGeo(value: string | string[] | null | undefined): string[] {
  if (!value) return [];
  const parts = Array.isArray(value) ? value : value.split(',');
  return parts.map((s) => s.trim()).filter(Boolean);
}

/** Canonical list: ANY is exclusive, codes upper-cased, sorted and de-duplicated. */
export function normalizeGeoList(codes: string[]): string[] {
  if (codes.includes(ANY_GEO)) return [ANY_GEO];
  return [...new Set(codes.map((c) => c.trim().toUpperCase()).filter(Boolean))].sort();
}

export function sameGeoList(a: string[], b: string[]): boolean {
  const x = normalizeGeoList(a), y = normalizeGeoList(b);
  return x.length === y.length && x.every((c, i) => c === y[i]);
}
